import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import { Accordion } from '@components/Clinicians/Accordion';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { isBlank } from '@helpers/htmlUtils';
import { confirmationModal } from 'vl-common/src/components/Modals';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';

const WorkingDiagnosisFormItem = () => {
  const { t, ready } = useTranslation('common');
  const appointment_id = useSessionAppointmentId();

  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted }
  } = useSessionAppointment();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);

  const isV2Appointment = detailsCurrentAppointment?.appt_version === 2;
  const isFollowUp = detailsCurrentAppointment?.appointment_type === 'Follow-up' || false;

  const handleNoteChange = useSessionNoteChangeHandler({ field: 'WD' });

  const [fieldValue, setFieldValue] = useSessionFormState('WD');
  const [isCheckedManually, setIsCheckedManually] = useState(false);

  const isEmptyFieldValue = Boolean(isBlank(fieldValue));

  const handleToggleAccordion = (checked: boolean) => {
    if (checked) {
      setIsCheckedManually(true);
      return;
    }

    if (isEmptyFieldValue) {
      setIsCheckedManually(false);
      return;
    }

    confirmationModal({
      title: 'Are you sure?',
      content: 'Your impression comments will be deleted',
      closable: true,
      onOk: () => {
        handleNoteChange({ field: 'WD', value: '' });
        setFieldValue(undefined);
        setIsCheckedManually(false);
      }
    });
  };

  const isExpanded = isCheckedManually || !isEmptyFieldValue;

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('WD');

  const getWDLabel = () => {
    if (isFollowUp) return t('clinicalUpdateLabel');
    if (isV2Appointment) return getNoteLabel('WD');
    return t('clinicalWDLabel');
  };

  return (
    <Accordion
      isCollapsibleByCheckbox={isV2Appointment}
      key={ready ? 'wd-form-item' : ''}
      field="wd"
      disabled={isCompleted || readOnly}
      label={getWDLabel()}
      required={isRequired}
      {...(isV2Appointment && { expanded: isExpanded, onChange: handleToggleAccordion })}
    >
      <NoteEditor
        data-testid="clinical-wd"
        field="WD"
        ariaLabel={getWDLabel()}
        placeholder={t('clinicalWDPlaceholder')}
        readOnly={readOnly || isCompleted}
        onChange={handleNoteChange}
      />
    </Accordion>
  );
};

export default WorkingDiagnosisFormItem;
